import { Box, HStack, VStack } from '@chakra-ui/react';
import { Button, Checkbox } from 'Atoms';
import { Modal } from 'Molecules';
import { Typography } from 'Tokens';
import { useUserSettings } from 'containers/Navigation';
import { useEffect, useState } from 'react';

export const MetricsAIDisclaimer = ({
  isOpen,
  onClose,
  onAIModalOpen,
  aiDisclaimerConfirmedAt,
  aiDoNotShowAgain,
  setAiDoNotShowAgain,
  isFromIcon,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAIModalOpen: () => void;
  aiDisclaimerConfirmedAt: string | null;
  aiDoNotShowAgain: boolean;
  setAiDoNotShowAgain: (val: boolean) => void;
  isFromIcon?: boolean;
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDoNotShowAgainConfirmed, setIsDoNotShowAgainConfirmed] = useState(false);
  const updateUserSettings = useUserSettings();

  useEffect(() => {
    setIsDoNotShowAgainConfirmed(aiDoNotShowAgain);
    setIsConfirmed(!!aiDisclaimerConfirmedAt);
  }, [aiDoNotShowAgain, aiDisclaimerConfirmedAt]);

  return (
    <Modal size="xxl" isOpen={isOpen} onClose={onClose} hasFooter={false}>
      <VStack height="100%">
        <VStack gap="24px" w="529px" alignItems="start" height="100%" justifyContent="center">
          <VStack alignItems="start" gap="8px">
            <Typography variant="h1">Generate answers with AI</Typography>
          </VStack>
          <Box bg="bg.interactive" p="16px" borderRadius="8px" width="460px">
            <VStack gap="8px">
              <Typography variant="body">
                This notice is to inform you that by using the following tool ESRS Reporting Module
                – AI Functionality, you are interacting with an artificial intelligence (AI) system,
                as defined under the EU AI Act.
              </Typography>
              <Typography variant="body">
                While we strive for accuracy, the generated output may not be 100% accurate, and you
                understand that it is your responsibility as the user to double check and confirm
                the accuracy of all output for the intended purpose.
              </Typography>
            </VStack>
          </Box>
          <VStack align="start">
            <HStack align="center" as="label" cursor="pointer" htmlFor="ai-confirm">
              <Checkbox
                id="ai-confirm"
                isChecked={isConfirmed}
                isDisabled={isFromIcon || aiDisclaimerConfirmedAt !== null}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                pointerEvents="none"
              />
              <Typography variant="body" userSelect="none">
                I confirm that I have read and understand the above information
              </Typography>
            </HStack>
            {!isFromIcon && (
              <HStack align="center" as="label" cursor="pointer" htmlFor="ai-dont-show">
                <Checkbox
                  id="ai-dont-show"
                  isChecked={isDoNotShowAgainConfirmed}
                  onChange={(e) => {
                    setIsDoNotShowAgainConfirmed(e.target.checked);
                  }}
                  pointerEvents="none"
                />
                <Typography variant="body" userSelect="none">
                  Do not show this message again
                </Typography>
              </HStack>
            )}
            {isFromIcon && (
              <Typography variant="detail" color="text.hint">
                {aiDisclaimerConfirmedAt
                  ? `(Confirmed on ${new Date(aiDisclaimerConfirmedAt).toLocaleDateString('de-DE')})`
                  : ''}
              </Typography>
            )}
          </VStack>
          {!isFromIcon && (
            <Button
              variant="primary"
              isDisabled={!isConfirmed}
              onClick={() => {
                onClose();
                onAIModalOpen();
                if (aiDisclaimerConfirmedAt !== null) {
                  setAiDoNotShowAgain(isDoNotShowAgainConfirmed);
                } else {
                  const timestamp = new Date().toISOString();
                  updateUserSettings({
                    'ai-disclaimer-confirmed-at': timestamp,
                    'ai-do-not-show-again': isDoNotShowAgainConfirmed,
                  });
                }
              }}
            >
              Use AI
            </Button>
          )}
        </VStack>
      </VStack>
    </Modal>
  );
};
